import { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import AppContext from '@/contexts/appContext';
import useAPI from '@/hooks/useAPI';
import usePagination from '@/hooks/usePagination';
import PersonRepository from '@/repositories/PersonRepository';
import { DIRECTION } from '@/types/sort';
import Alert from '@/ui/components/Alert';
import Heading from '@/ui/components/Heading';
import Paginator from '@/ui/components/Paginator';
import Spinner from '@/ui/components/Spinner';

import { AllPeopleFilterForm, PersonRow, allPeopleDefaultSearchParams } from './components';

const AllPeople = () => {
  const { addMessage, currentUser } = useContext(AppContext);
  const personRepository = PersonRepository();

  const [searchParams, setSearchParams] = useSearchParams();
  const activeParams = {
    direction: searchParams.get('direction') ?? allPeopleDefaultSearchParams.direction,
    ordering: searchParams.get('ordering') ?? allPeopleDefaultSearchParams.ordering,
    page: searchParams.get('page') ?? allPeopleDefaultSearchParams.page,
    search: searchParams.get('search') ?? allPeopleDefaultSearchParams.search
  };

  const [totalPages, setTotalPages] = useState<number>(0);

  const { data, isError, isPending, isSuccess } = useAPI({
    queryKey: ['getPeople', currentUser?.id, activeParams],
    queryFn: async () => {
      const { direction, ordering, page, search } = activeParams;
      return await personRepository.getPeople({
        ordering: `${direction === DIRECTION.ASC ? '' : '-'}${ordering}`,
        page,
        ...(search && { search })
      });
    },
    enabled: !!currentUser?.id && activeParams.search !== '' // Only fetch if there's a search term
  });

  useEffect(() => {
    if (isSuccess && data.data?.total_pages) {
      setTotalPages(data.data.total_pages);
    }
  }, [addMessage, data?.data?.total_pages, isSuccess]);

  const { currentPage, goToPage, nextPage, prevPage } = usePagination(
    +activeParams.page,
    totalPages
  );

  useEffect(() => {
    if (currentPage !== +allPeopleDefaultSearchParams.page)
      setSearchParams((currentParams) => {
        currentParams.set('page', currentPage.toString());
        return currentParams;
      });
    else
      setSearchParams((currentParams) => {
        currentParams.delete('page');
        return currentParams;
      });
    // setSearchParams dependency was causing infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  if (!currentUser?.id)
    return (
      <Alert level="error">Unable to identify the current Dugout user to get all people</Alert>
    );

  return (
    <>
      <AllPeopleFilterForm activeParams={activeParams} setParams={setSearchParams} />

      {activeParams.search === '' ? (
        <div className="text-center p-8">
          <Heading text="Enter a search term to find people" value={3} />
        </div>
      ) : isPending ? (
        <div className="text-center w-full">
          <Spinner />
        </div>
      ) : !isError && data.data?.results?.length ? (
        <ul className="mt-2">
          {data.data.results.map((person) => (
            <li key={person.id}>
              <PersonRow person={person} />
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-center p-8">
          <Heading text="No people found (or your search didn't return results)" value={3} />
        </div>
      )}

      <Paginator
        currentPage={currentPage}
        goToPage={goToPage}
        nextPage={nextPage}
        prevPage={prevPage}
        totalPages={totalPages}
      />
    </>
  );
};

export default AllPeople;
